// Hausgeldkonten eines Eigentümers (E7) aus dem gemeinsamen Mietkonto: je WEG-Objekt das Konto
// eigentuemerKonto(eigentümer, einheit). Dieselben Zahlen wie Mieteingang/Mahnwesen am Objekt.

import { useEffect, useMemo, useState } from 'react';
import { heuteIso } from '../immobilien/format';
import { ladeMietkontoDaten, type MietkontoLaden } from '../mietkonto/api';
import { baueMietkonto } from '../mietkonto/mietkonto';
import { eigentuemerKonto, type LedgerEntry } from '../mietkonto/typen';
import { MIETKONTO_GEAENDERT } from '../mietkonto/useMietkonto';

export interface HausgeldKonto { objektId: string; einheitId: string; konto: LedgerEntry | null }

export type HausgeldZustand =
  | { status: 'laedt' }
  | { status: 'nicht_eingerichtet' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; konten: HausgeldKonto[]; saldo: number };

export function useHausgeldKonten(eigId: string, einheiten: readonly { id: string; objektId: string }[]): HausgeldZustand {
  const [laden, setLaden] = useState<Map<string, MietkontoLaden> | null>(null);
  const [runde, setRunde] = useState(0);
  const objekte = [...new Set(einheiten.map(e => e.objektId))].sort().join(',');

  useEffect(() => {
    if (!objekte) { setLaden(new Map()); return; }
    let aktiv = true;
    setLaden(null);
    const ids = objekte.split(',');
    Promise.all(ids.map(async id => [id, await ladeMietkontoDaten(id)] as const))
      .then(e => { if (aktiv) setLaden(new Map(e)); })
      .catch((err: unknown) => { if (aktiv) setLaden(new Map([['', { status: 'fehler', meldung: err instanceof Error ? err.message : 'Unbekannter Fehler' }]])); });
    return () => { aktiv = false; };
  }, [objekte, runde]);

  useEffect(() => {
    const bei = (e: Event) => { const id = (e as CustomEvent<string>).detail; if (!id || objekte.split(',').includes(id)) setRunde(r => r + 1); };
    window.addEventListener(MIETKONTO_GEAENDERT, bei);
    return () => window.removeEventListener(MIETKONTO_GEAENDERT, bei);
  }, [objekte]);

  const heute = heuteIso();
  return useMemo<HausgeldZustand>(() => {
    if (!laden) return { status: 'laedt' };
    const werte = [...laden.values()];
    const fehler = werte.find((l): l is Extract<MietkontoLaden, { status: 'fehler' }> => l.status === 'fehler');
    if (fehler) return { status: 'fehler', meldung: fehler.meldung };
    if (werte.length === 0 || werte.some(l => l.status === 'nicht_eingerichtet')) return { status: 'nicht_eingerichtet' };
    const konten = einheiten.map(e => {
      const l = laden.get(e.objektId);
      const konto = l && l.status === 'ok' ? baueMietkonto({ ...l.daten, heute }).map[eigentuemerKonto(eigId, e.id)] ?? null : null;
      return { objektId: e.objektId, einheitId: e.id, konto };
    });
    return { status: 'ok', konten, saldo: Math.round(konten.reduce((s, k) => s + (k.konto?.saldo ?? 0), 0) * 100) / 100 };
  }, [laden, einheiten, eigId, heute]);
}
